import type { AppState, AvailabilitySchedule, EventType, IntakeForm, Weekday } from "./types";

const weekdays: Weekday[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export const defaultAvailability: AvailabilitySchedule = {
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago",
  weekly: Object.fromEntries(
    weekdays.map((day) => [
      day,
      {
        enabled: !["sun", "sat"].includes(day),
        ranges: [{ start: "09:00", end: "17:00" }]
      }
    ])
  ) as AvailabilitySchedule["weekly"],
  overrides: []
};

const now = new Date().toISOString();

export const defaultEventTypes: EventType[] = [
  {
    id: "intro-call",
    name: "30 Minute Intro Call",
    slug: "intro-call",
    description: "A focused introductory call to discuss your goals and next steps.",
    durationMinutes: 30,
    locationType: "google_meet",
    locationLabel: "Google Meet",
    color: "#6d5dfc",
    active: true,
    minimumNoticeHours: 4,
    bufferBeforeMinutes: 0,
    bufferAfterMinutes: 10,
    bookingWindowDays: 60,
    questions: [
      { id: "goal", label: "What would you like to discuss?", required: false, type: "textarea" }
    ],
    intakeMode: "native_form",
    intakeFormId: "consultation-application",
    externalFormUrl: "",
    externalFormPresentation: "redirect",
    createdAt: now,
    updatedAt: now
  },
  {
    id: "strategy-session",
    name: "60 Minute Strategy Session",
    slug: "strategy-session",
    description: "A deeper working session for planning, review, or implementation strategy.",
    durationMinutes: 60,
    locationType: "google_meet",
    locationLabel: "Google Meet",
    color: "#0f9d76",
    active: true,
    minimumNoticeHours: 12,
    bufferBeforeMinutes: 10,
    bufferAfterMinutes: 15,
    bookingWindowDays: 45,
    questions: [],
    intakeMode: "direct",
    intakeFormId: "",
    externalFormUrl: "",
    externalFormPresentation: "redirect",
    createdAt: now,
    updatedAt: now
  }
];

export const defaultForms: IntakeForm[] = [
  {
    id: "consultation-application",
    name: "Consultation Application",
    slug: "consultation-application",
    description: "A short application to make sure the call is the right next step.",
    active: true,
    mode: "native",
    externalUrl: "",
    externalPresentation: "redirect",
    submitLabel: "Continue",
    questions: [
      {
        id: "full-name",
        type: "short_text",
        title: "What is your name?",
        description: "",
        required: true,
        placeholder: "Your full name",
        options: [],
        minLength: 2,
        maxLength: 100,
        contactField: "name"
      },
      {
        id: "email",
        type: "email",
        title: "What is your email address?",
        description: "We will use this for the calendar invitation.",
        required: true,
        placeholder: "you@example.com",
        options: [],
        contactField: "email"
      },
      {
        id: "monthly-revenue",
        type: "single_choice",
        title: "What is your current monthly revenue?",
        description: "Choose the closest range.",
        required: true,
        placeholder: "",
        options: ["Under $1,000", "$1,000–$5,000", "$5,000–$10,000", "Over $10,000"],
        contactField: "none"
      },
      {
        id: "goal",
        type: "long_text",
        title: "What would you like help with?",
        description: "A few sentences is enough.",
        required: true,
        placeholder: "Tell us about your situation and desired outcome.",
        options: [],
        minLength: 10,
        maxLength: 1500,
        contactField: "none"
      }
    ],
    rules: [
      {
        id: "low-revenue-review",
        name: "Review lower-revenue applications",
        match: "all",
        conditions: [
          {
            id: "low-revenue-condition",
            questionId: "monthly-revenue",
            operator: "equals",
            value: "Under $1,000"
          }
        ],
        action: {
          type: "manual_review",
          eventTypeId: "",
          message: "Thanks for applying. We will review your answers and follow up by email.",
          redirectUrl: ""
        }
      }
    ],
    defaultAction: {
      type: "allow_booking",
      eventTypeId: "intro-call",
      message: "You qualify. Choose a time for your call.",
      redirectUrl: ""
    },
    createdAt: now,
    updatedAt: now
  }
];

export const defaultState: AppState = {
  eventTypes: defaultEventTypes,
  forms: defaultForms,
  bookings: [],
  submissions: [],
  availability: defaultAvailability,
  branding: {
    businessName: "MoreJpegs",
    logoDataUrl: "",
    primaryColor: "#1e1e2d",
    accentColor: "#1b84ff",
    welcomeMessage: "Choose an event and select a time that works for you."
  }
};
